/*
 * SULFUR SYSTEM — interface behaviours layered on top of Dawn.
 * No dependencies. Everything is progressive: the page works with this file missing,
 * and every motion effect is skipped when the visitor asks for reduced motion.
 */
(() => {
  'use strict';
  if (window.Sulfur) return;

  const root = document.documentElement;
  const reduceQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const Sulfur = (window.Sulfur = {
    get reduced() {
      return reduceQuery.matches || !root.classList.contains('sulfur-motion');
    },
    finePointer: window.matchMedia('(hover: hover) and (pointer: fine)'),
    clamp: (v, min, max) => Math.min(max, Math.max(min, v)),
    /* Run fn once per animation frame at most, for scroll and pointer handlers */
    frame(fn) {
      let queued = false;
      return (...args) => {
        if (queued) return;
        queued = true;
        requestAnimationFrame(() => {
          queued = false;
          fn(...args);
        });
      };
    },
    /* Call enter(el) the first time el comes into view */
    onceVisible(el, enter, options) {
      if (!('IntersectionObserver' in window)) return enter(el);
      const io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          io.disconnect();
          enter(el);
        });
      }, options || { rootMargin: '0px 0px -12% 0px' });
      io.observe(el);
    },
    /* Call toggle(el, visible) every time visibility changes */
    whileVisible(el, toggle, options) {
      if (!('IntersectionObserver' in window)) return toggle(el, true);
      const io = new IntersectionObserver((entries) => entries.forEach((e) => toggle(el, e.isIntersecting)), options || {});
      io.observe(el);
      return io;
    },
  });

  /* Scroll state: lets the header go from transparent to solid on the home page */
  const setScrolled = Sulfur.frame(() => root.classList.toggle('is-scrolled', window.scrollY > 24));
  window.addEventListener('scroll', setScrolled, { passive: true });
  setScrolled();

  /* Keep --header-height in sync: the hero slides under the header by exactly that amount */
  const trackHeader = () => {
    const header = document.querySelector('.section-header');
    if (!header) return;
    const apply = () => root.style.setProperty('--header-height', `${header.offsetHeight}px`);
    apply();
    if ('ResizeObserver' in window) new ResizeObserver(apply).observe(header);
  };

  const ready = () => {
    trackHeader();
    requestAnimationFrame(() => root.classList.add('is-ready'));
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', ready);
  else ready();
})();
/* Ticker: the loop pauses while it is off screen */
(() => {
  'use strict';
  if (customElements.get('sulfur-ticker')) return;

  customElements.define(
    'sulfur-ticker',
    class extends HTMLElement {
      connectedCallback() {
        this.io = window.Sulfur.whileVisible(this, (el, visible) => el.classList.toggle('is-offscreen', !visible));
      }

      disconnectedCallback() {
        if (this.io) this.io.disconnect();
      }
    }
  );
})();
/* Hero: decoding title, storm layer loaded on demand, copyable promo code */
(() => {
  'use strict';
  const Sulfur = window.Sulfur;
  const GLYPHS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789/<>[]+=#';

  /* Replace the letters of el with shifting glyphs that settle from left to right */
  Sulfur.decode = (el, duration = 900, delay = 0) => {
    if (Sulfur.reduced || el.dataset.decoding) return;
    const targets = el.children.length ? [...el.querySelectorAll(':scope > span')] : [el];
    if (!el.hasAttribute('aria-label')) el.setAttribute('aria-label', el.textContent.replace(/\s+/g, ' ').trim());
    el.dataset.decoding = 'true';
    let pending = targets.length;
    targets.forEach((node, index) => {
      const text = node.dataset.sText || node.textContent;
      node.dataset.sText = text;
      if (node !== el) node.setAttribute('aria-hidden', 'true');
      const begin = performance.now() + delay + index * 120;
      const step = (now) => {
        const progress = Sulfur.clamp((now - begin) / duration, 0, 1);
        if (progress <= 0) return requestAnimationFrame(step);
        const settled = Math.floor(progress * text.length);
        let out = text.slice(0, settled);
        for (let i = settled; i < text.length; i++) out += /\s/.test(text[i]) ? text[i] : GLYPHS[(Math.random() * GLYPHS.length) | 0];
        node.textContent = out;
        if (progress < 1) return requestAnimationFrame(step);
        node.textContent = text;
        if (--pending === 0) delete el.dataset.decoding;
      };
      requestAnimationFrame(step);
    });
  };

  if (!customElements.get('sulfur-hero')) {
    customElements.define(
      'sulfur-hero',
      class extends HTMLElement {
        connectedCallback() {
          const title = this.querySelector('[data-s-decode]');
          if (title) {
            // on a first visit the boot shutters are still closed for a little over a second
            const boot = document.documentElement.classList.contains('sulfur-boot') ? 1250 : 0;
            Sulfur.decode(title, 800, this.classList.contains('s-hero--first') ? boot + 420 : 0);
          }
          this.canvas = this.querySelector('.s-hero__storm');
          if (!this.canvas || !this.dataset.storm || Sulfur.reduced) return;
          if (navigator.connection && navigator.connection.saveData) return;
          this.io = Sulfur.whileVisible(this, (el, visible) => {
            this.visible = visible;
            if (visible && !this.storm && !this.loading) this.load();
            else this.sync();
          });
          this.onVisibility = () => this.sync();
          document.addEventListener('visibilitychange', this.onVisibility);
        }

        load() {
          this.loading = true;
          const boot = () => {
            try {
              this.storm = new window.SulfurStorm(this.canvas, this, this.dataset.storm);
              this.classList.add('is-storming');
              this.sync();
            } catch (error) {
              this.canvas.remove();
            }
          };
          if (window.SulfurStorm) return boot();
          const script = document.createElement('script');
          script.src = this.dataset.stormSrc;
          script.async = true;
          script.onload = boot;
          document.head.appendChild(script);
        }

        sync() {
          if (!this.storm) return;
          if (this.visible && !document.hidden) this.storm.play();
          else this.storm.stop();
        }

        disconnectedCallback() {
          if (this.io) this.io.disconnect();
          if (this.storm) this.storm.destroy();
          document.removeEventListener('visibilitychange', this.onVisibility);
        }
      }
    );
  }

  if (!customElements.get('sulfur-copy')) {
    customElements.define(
      'sulfur-copy',
      class extends HTMLElement {
        connectedCallback() {
          this.button = this.querySelector('button');
          this.action = this.querySelector('.s-copy__action');
          this.status = this.querySelector('[role="status"]');
          if (this.button) this.button.addEventListener('click', () => this.copy());
        }

        async copy() {
          const value = this.dataset.value || '';
          try {
            await navigator.clipboard.writeText(value);
          } catch (error) {
            const field = document.createElement('textarea');
            field.value = value;
            field.setAttribute('readonly', '');
            field.style.cssText = 'position:fixed;opacity:0;pointer-events:none';
            document.body.appendChild(field);
            field.select();
            try {
              document.execCommand('copy');
            } catch (ignored) {
              /* nothing else to try: the code stays visible for manual copy */
            }
            field.remove();
          }
          this.classList.add('is-copied');
          if (this.action) this.action.textContent = this.action.dataset.done;
          if (this.status) this.status.textContent = `${this.action ? this.action.dataset.done : ''} : ${value}`;
          clearTimeout(this.timer);
          this.timer = setTimeout(() => {
            this.classList.remove('is-copied');
            if (this.action) this.action.textContent = this.action.dataset.idle;
            if (this.status) this.status.textContent = '';
          }, 2200);
        }
      }
    );
  }
})();
/* Section behaviours: decoding titles, rail, index preview, pinned gallery, lit words */
(() => {
  'use strict';
  const Sulfur = window.Sulfur;

  /* Section titles decode once, the first time they are seen */
  const armTitles = (scope) => {
    scope.querySelectorAll('[data-s-decode]:not([data-s-armed])').forEach((el) => {
      el.dataset.sArmed = 'true';
      if (el.closest('sulfur-hero')) return el.classList.add('is-in');
      Sulfur.onceVisible(el, () => {
        el.classList.add('is-in');
        Sulfur.decode(el, 600);
      });
    });
  };
  armTitles(document);
  document.addEventListener('shopify:section:load', (event) => armTitles(event.target));

  /* Rail: mouse drag, previous/next buttons and a progress line on top of native scrolling */
  if (!customElements.get('sulfur-rail')) {
    customElements.define(
      'sulfur-rail',
      class extends HTMLElement {
        connectedCallback() {
          if (this.hasAttribute('data-static')) return;
          this.track = this.querySelector('.s-rail__track');
          this.prev = this.querySelector('[data-rail-prev]');
          this.next = this.querySelector('[data-rail-next]');
          this.bar = this.querySelector('.s-rail__progress span');
          if (!this.track) return;
          this.update = Sulfur.frame(() => this.measure());
          this.track.addEventListener('scroll', this.update, { passive: true });
          this.resizer = 'ResizeObserver' in window ? new ResizeObserver(this.update) : null;
          if (this.resizer) this.resizer.observe(this.track);
          if (this.prev) this.prev.addEventListener('click', () => this.step(-1));
          if (this.next) this.next.addEventListener('click', () => this.step(1));
          this.drag();
          this.measure();
        }

        disconnectedCallback() {
          if (this.resizer) this.resizer.disconnect();
        }

        measure() {
          const max = this.track.scrollWidth - this.track.clientWidth;
          const x = this.track.scrollLeft;
          const visible = this.track.clientWidth / this.track.scrollWidth;
          const progress = max > 0 ? visible + (1 - visible) * (x / max) : 1;
          if (this.bar) this.bar.style.setProperty('--s-rail-progress', progress.toFixed(4));
          if (this.prev) this.prev.disabled = x <= 2;
          if (this.next) this.next.disabled = x >= max - 2;
          this.classList.toggle('is-scrollable', max > 2);
        }

        step(direction) {
          const item = this.track.querySelector('.s-rail__item');
          const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
          const width = item ? item.getBoundingClientRect().width + gap : this.track.clientWidth;
          const count = Math.max(1, Math.floor((this.track.clientWidth * 0.8) / width));
          this.track.scrollBy({ left: direction * width * count, behavior: Sulfur.reduced ? 'auto' : 'smooth' });
        }

        drag() {
          let startX = 0;
          let startLeft = 0;
          let moved = 0;
          let active = false;
          const track = this.track;
          track.addEventListener('pointerdown', (event) => {
            if (event.pointerType !== 'mouse' || event.button !== 0) return;
            if (event.target.closest('button, input, select, textarea, quick-add-modal')) return;
            active = true;
            moved = 0;
            startX = event.clientX;
            startLeft = track.scrollLeft;
          });
          window.addEventListener('pointermove', (event) => {
            if (!active) return;
            const delta = event.clientX - startX;
            moved = Math.max(moved, Math.abs(delta));
            if (moved > 6) {
              track.classList.add('is-dragging');
              track.scrollLeft = startLeft - delta;
            }
          });
          const end = () => {
            if (!active) return;
            active = false;
            if (!track.classList.contains('is-dragging')) return;
            // let the click that ends the drag be swallowed before links work again
            setTimeout(() => track.classList.remove('is-dragging'), 0);
          };
          window.addEventListener('pointerup', end);
          window.addEventListener('pointercancel', end);
          track.addEventListener('dragstart', (event) => event.preventDefault());
          track.addEventListener(
            'click',
            (event) => {
              if (moved > 6) {
                event.preventDefault();
                event.stopPropagation();
                moved = 0;
              }
            },
            true
          );
        }
      }
    );
  }

  /* Index: the row being aimed at loads its collection into the viewer */
  if (!customElements.get('sulfur-index')) {
    customElements.define(
      'sulfur-index',
      class extends HTMLElement {
        connectedCallback() {
          this.items = [...this.querySelectorAll('[data-index-preview]')];
          this.rows = [...this.querySelectorAll('[data-index-row]')];
          if (!this.items.length) return;
          this.current = Math.max(0, this.items.findIndex((item) => item.classList.contains('is-current')));
          this.rows.forEach((row) => {
            const show = () => this.show(Number(row.dataset.indexRow));
            row.addEventListener('pointerenter', show);
            row.addEventListener('focus', show);
          });
        }

        show(index) {
          if (index === this.current || !this.items[index]) return;
          const previous = this.current;
          this.current = index;
          this.items.forEach((item, i) => {
            item.classList.toggle('is-previous', i === previous);
            item.classList.toggle('is-current', i === index);
          });
          this.rows.forEach((row) => {
            const line = row.closest('.s-index__row');
            if (line) line.classList.toggle('is-current', Number(row.dataset.indexRow) === index);
          });
        }
      }
    );
  }

  /* Universe: vertical scrolling drives the gallery sideways while it is pinned */
  if (!customElements.get('sulfur-universe')) {
    customElements.define(
      'sulfur-universe',
      class extends HTMLElement {
        connectedCallback() {
          this.track = this.querySelector('.s-uni__track');
          this.viewport = this.querySelector('.s-uni__viewport');
          this.panels = [...this.querySelectorAll('.s-uni__panel')];
          this.current = this.querySelector('[data-uni-current]');
          this.progress = this.querySelector('[data-uni-progress]');
          if (!this.track || !this.panels.length) return;
          this.query = window.matchMedia('(min-width: 990px) and (hover: hover) and (pointer: fine)');
          this.onScroll = Sulfur.frame(() => this.render());
          this.setup = this.setup.bind(this);
          this.query.addEventListener('change', this.setup);
          window.addEventListener('resize', Sulfur.frame(this.setup));
          this.viewport.addEventListener('scroll', Sulfur.frame(() => this.markActive()), { passive: true });
          // keyboard users can land on a card that is off screen: bring it back into view
          this.track.addEventListener('focusin', (event) => this.reveal(event.target.closest('.s-uni__panel')));
          this.setup();
          if (document.fonts && document.fonts.ready) document.fonts.ready.then(this.setup);
        }

        disconnectedCallback() {
          window.removeEventListener('scroll', this.onScroll);
        }

        setup() {
          const pin = this.query.matches && !Sulfur.reduced && !(window.Shopify && window.Shopify.designMode);
          window.removeEventListener('scroll', this.onScroll);
          if (!pin) {
            this.classList.remove('is-pinned');
            this.style.removeProperty('--s-uni-height');
            this.style.removeProperty('--s-uni-x');
            this.markActive();
            return;
          }
          this.classList.add('is-pinned');
          this.distance = Math.max(0, this.track.scrollWidth - window.innerWidth);
          this.style.setProperty('--s-uni-height', `${window.innerHeight + this.distance}px`);
          window.addEventListener('scroll', this.onScroll, { passive: true });
          this.render();
        }

        render() {
          if (!this.classList.contains('is-pinned')) return;
          const rect = this.getBoundingClientRect();
          const t = this.distance > 0 ? Sulfur.clamp(-rect.top / this.distance, 0, 1) : 0;
          this.style.setProperty('--s-uni-x', `${(-t * this.distance).toFixed(1)}px`);
          this.style.setProperty('--s-uni-progress', t.toFixed(4));
          this.markActive();
        }

        markActive() {
          const centre = window.innerWidth / 2;
          let best = 0;
          let bestDistance = Infinity;
          const pinned = this.classList.contains('is-pinned');
          this.panels.forEach((panel, index) => {
            const rect = panel.getBoundingClientRect();
            const distance = Math.abs(rect.left + rect.width / 2 - centre);
            if (pinned) panel.style.setProperty('--s-par', Sulfur.clamp((rect.left + rect.width / 2 - centre) / window.innerWidth, -1, 1).toFixed(3));
            if (distance < bestDistance) {
              bestDistance = distance;
              best = index;
            }
          });
          this.panels.forEach((panel, index) => panel.classList.toggle('is-active', index === best));
          if (this.current) this.current.textContent = String(best + 1).padStart(2, '0');
          if (!this.classList.contains('is-pinned') && this.progress) {
            const max = this.viewport.scrollWidth - this.viewport.clientWidth;
            this.style.setProperty('--s-uni-progress', max > 0 ? (this.viewport.scrollLeft / max).toFixed(4) : '0');
          }
        }

        reveal(panel) {
          if (!panel || !this.classList.contains('is-pinned') || !this.distance) return;
          const offset = panel.offsetLeft - (window.innerWidth - panel.offsetWidth) / 2;
          const t = Sulfur.clamp(offset / this.distance, 0, 1);
          const top = this.getBoundingClientRect().top + window.scrollY;
          window.scrollTo({ top: top + t * this.distance, behavior: 'auto' });
        }
      }
    );
  }

  /* Manifesto: split the statement into words and light them with the scroll position */
  if (!customElements.get('sulfur-words')) {
    customElements.define(
      'sulfur-words',
      class extends HTMLElement {
        connectedCallback() {
          if (Sulfur.reduced || this.words) return;
          const paragraph = this.querySelector('p');
          if (!paragraph) return;
          const walker = document.createTreeWalker(paragraph, NodeFilter.SHOW_TEXT);
          const nodes = [];
          while (walker.nextNode()) nodes.push(walker.currentNode);
          nodes.forEach((node) => {
            const fragment = document.createDocumentFragment();
            node.textContent.split(/(\s+)/).forEach((part) => {
              if (!part) return;
              if (/^\s+$/.test(part)) return fragment.appendChild(document.createTextNode(' '));
              const span = document.createElement('span');
              span.className = 's-word';
              span.textContent = part;
              fragment.appendChild(span);
            });
            node.replaceWith(fragment);
          });
          this.words = [...paragraph.querySelectorAll('.s-word')];
          this.classList.add('is-enhanced');
          this.onScroll = Sulfur.frame(() => this.render());
          window.addEventListener('scroll', this.onScroll, { passive: true });
          window.addEventListener('resize', this.onScroll);
          this.render();
        }

        disconnectedCallback() {
          window.removeEventListener('scroll', this.onScroll);
          window.removeEventListener('resize', this.onScroll);
        }

        render() {
          const rect = this.getBoundingClientRect();
          const start = window.innerHeight * 0.85;
          const end = window.innerHeight * 0.35;
          const t = Sulfur.clamp((start - rect.top) / (start - end + rect.height), 0, 1);
          const lit = Math.round(t * this.words.length);
          if (lit === this.lit) return;
          this.lit = lit;
          this.words.forEach((word, index) => word.classList.toggle('is-lit', index < lit));
        }
      }
    );
  }
})();
/* Product page: sticky buy bar, tidier descriptions */
(() => {
  'use strict';
  const Sulfur = window.Sulfur;
  const french = (document.documentElement.lang || 'fr').toLowerCase().startsWith('fr');

  /* Wide tables scroll inside their own box; the size guide folds into an accordion */
  const tidyDescriptions = (scope) => {
    scope.querySelectorAll('.rte table:not([data-s-tidy])').forEach((table) => {
      table.dataset.sTidy = 'true';
      const wrap = document.createElement('div');
      wrap.className = 's-table-scroll';
      table.replaceWith(wrap);
      wrap.appendChild(table);
      if (!table.classList.contains('size-guide-table') || !wrap.closest('.product__description')) return;
      const accordion = document.createElement('div');
      accordion.className = 'accordion s-size-guide';
      accordion.innerHTML =
        '<details><summary><div class="summary__title"><h2 class="h4 accordion__title"></h2></div>' +
        '<svg class="icon icon-caret" viewBox="0 0 10 6" aria-hidden="true" focusable="false"><path fill-rule="evenodd" clip-rule="evenodd" d="M9.354.646a.5.5 0 0 0-.708 0L5 4.293 1.354.646a.5.5 0 0 0-.708.708l4 4a.5.5 0 0 0 .708 0l4-4a.5.5 0 0 0 0-.708" fill="currentColor"/></svg>' +
        '</summary><div class="accordion__content"></div></details>';
      accordion.querySelector('.accordion__title').textContent = french ? 'Guide des tailles' : 'Size guide';
      wrap.replaceWith(accordion);
      accordion.querySelector('.accordion__content').appendChild(wrap);
    });
  };

  /* A compact bar that keeps the add-to-cart button within reach while reading the page */
  const buyBar = () => {
    const info = document.querySelector('product-info[id^="MainProduct-"]');
    if (!info || !document.body.classList.contains('s-template-product') || document.querySelector('.s-buybar')) return;
    const submit = info.querySelector('.product-form__submit');
    const title = info.querySelector('.product__title h1');
    if (!submit || !title) return;

    const bar = document.createElement('div');
    bar.className = 's-buybar';
    bar.setAttribute('aria-hidden', 'true');
    bar.innerHTML =
      '<div class="s-buybar__thumb"></div>' +
      '<div class="s-buybar__info"><span class="s-buybar__title"></span><span class="s-buybar__price"></span></div>' +
      '<button type="button" class="s-buybar__button button" tabindex="-1"></button>';
    const button = bar.querySelector('button');
    const priceSlot = bar.querySelector('.s-buybar__price');
    bar.querySelector('.s-buybar__title').textContent = title.textContent.trim();

    const sync = () => {
      const label = submit.querySelector('span');
      button.textContent = (label ? label.textContent : submit.textContent).trim();
      button.disabled = submit.disabled || submit.getAttribute('aria-disabled') === 'true';
      const price = info.querySelector('[id^="price-"] .price');
      if (price) {
        const sale = price.classList.contains('price--on-sale');
        const item = price.querySelector(sale ? '.price__sale .price-item--sale' : '.price__regular .price-item--regular');
        priceSlot.textContent = item ? item.textContent.trim() : '';
      }
      const image = info.querySelector('.product__media-item.is-active img, .product__media-item img');
      const thumb = bar.querySelector('.s-buybar__thumb');
      if (image && thumb.dataset.src !== image.src) {
        thumb.dataset.src = image.src;
        thumb.innerHTML = '';
        const copy = new Image();
        copy.alt = '';
        copy.src = image.currentSrc || image.src;
        thumb.appendChild(copy);
      }
    };
    sync();
    new MutationObserver(Sulfur.frame(sync)).observe(info, { subtree: true, childList: true, attributes: true, attributeFilter: ['disabled', 'aria-disabled', 'class'] });

    button.addEventListener('click', () => submit.click());
    document.body.appendChild(bar);

    const show = (visible) => {
      bar.classList.toggle('is-visible', visible);
      bar.setAttribute('aria-hidden', String(!visible));
      button.tabIndex = visible ? 0 : -1;
    };
    // shown once the main button has scrolled away, hidden again when the footer arrives
    let past = false;
    let atFooter = false;
    const update = () => show(past && !atFooter);
    new IntersectionObserver((entries) => {
      entries.forEach((entry) => (past = !entry.isIntersecting && entry.boundingClientRect.top < 0));
      update();
    }).observe(submit);
    const footer = document.querySelector('.shopify-section-group-footer-group');
    if (footer) {
      new IntersectionObserver((entries) => {
        entries.forEach((entry) => (atFooter = entry.isIntersecting));
        update();
      }).observe(footer);
    }
  };

  /* Descriptions written as "Label : value" lines become a specification sheet */
  const specSheets = (scope) => {
    scope.querySelectorAll('.product__description:not([data-s-specs])').forEach((box) => {
      box.dataset.sSpecs = 'true';
      const read = (p) => {
        if (p.tagName !== 'P' || p.querySelector('img, iframe, a, table')) return null;
        const text = p.textContent.replace(/\s+/g, ' ').trim();
        const match = /^([^:]{2,48}?)\s*:\s*(.{1,120})$/.exec(text);
        return match ? [match[1].trim(), match[2].trim()] : null;
      };
      let run = [];
      const flush = () => {
        // a single "Label : value" line stays a sentence; two or more make a sheet
        if (run.length >= 2) {
          const list = document.createElement('dl');
          list.className = 's-specs';
          run.forEach(([p, pair]) => {
            const term = document.createElement('dt');
            term.textContent = pair[0];
            const value = document.createElement('dd');
            value.textContent = pair[1];
            list.append(term, value);
          });
          run[0][0].before(list);
          run.forEach(([p]) => p.remove());
        }
        run = [];
      };
      [...box.children].forEach((child) => {
        const pair = read(child);
        if (pair) run.push([child, pair]);
        else if (child.tagName === 'P' && !child.textContent.trim() && !child.children.length) return;
        else flush();
      });
      flush();
    });
  };

  /* Added to cart: the cart icon answers, and the phone gives a short tap where it can */
  const cartFeedback = () => {
    // Dawn's pubsub.js and constants.js declare these as plain globals
    /* global subscribe, PUB_SUB_EVENTS */
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (!event || event.source !== 'product-form') return;
      const icon = document.getElementById('cart-icon-bubble');
      if (icon) {
        icon.classList.remove('is-bumped');
        void icon.offsetWidth;
        icon.classList.add('is-bumped');
      }
      if (document.documentElement.classList.contains('sulfur-haptics') && navigator.vibrate) {
        try {
          navigator.vibrate(12);
        } catch (error) {
          /* vibration is a bonus, never a requirement */
        }
      }
    });
  };

  const init = () => {
    tidyDescriptions(document);
    specSheets(document);
    buyBar();
    cartFeedback();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', (event) => {
    tidyDescriptions(event.target);
    specSheets(event.target);
  });
})();
/* Product gallery, large screens: a measuring rail in the page margin, left of the photograph.
   Large index, one notch per image, a lime cursor that slides to the current one, previous and
   next controls. It drives Dawn's own thumbnails, so switching, zoom and variants keep working. */
(() => {
  'use strict';
  const Sulfur = window.Sulfur;
  const GAP = 8;
  const svg = (d) => `<svg viewBox="0 0 12 12" aria-hidden="true" focusable="false"><path d="${d}" fill="none" stroke="currentColor" stroke-width="1.4"/></svg>`;

  const build = (gallery) => {
    if (gallery.dataset.sRail) return;
    const list = gallery.querySelector('.product__media-list');
    const thumbs = [...gallery.querySelectorAll('.thumbnail-list .thumbnail-list__item button.thumbnail')];
    const items = list ? [...list.querySelectorAll(':scope > .product__media-item')] : [];
    if (!list || items.length < 2 || !thumbs.length) return;
    gallery.dataset.sRail = 'true';
    const pad = (n) => String(n).padStart(2, '0');

    const rail = document.createElement('div');
    rail.className = 's-gallery-rail';
    rail.style.setProperty('--s-rail-n', items.length);
    rail.innerHTML =
      `<div class="s-gallery-rail__index" aria-hidden="true"><span class="s-gallery-rail__current">01</span><span class="s-gallery-rail__total">/${pad(items.length)}</span></div>` +
      '<div class="s-gallery-rail__track"><span class="s-gallery-rail__cursor"></span></div>' +
      `<button type="button" class="s-gallery-rail__step" data-step="-1" aria-label="Image précédente">${svg('M2 8l4-4 4 4')}</button>` +
      `<button type="button" class="s-gallery-rail__step" data-step="1" aria-label="Image suivante">${svg('M2 4l4 4 4-4')}</button>`;
    const track = rail.querySelector('.s-gallery-rail__track');
    const number = rail.querySelector('.s-gallery-rail__current');
    const prev = rail.querySelector('[data-step="-1"]');
    const next = rail.querySelector('[data-step="1"]');

    let current = 0;
    const thumbFor = (index) => {
      const id = items[index] && items[index].dataset.mediaId;
      return thumbs.find((button) => {
        const holder = button.closest('[data-target]');
        return holder && holder.dataset.target === id;
      }) || thumbs[index];
    };
    const go = (index) => {
      const thumb = thumbFor(Sulfur.clamp(index, 0, items.length - 1));
      if (thumb) thumb.click();
    };

    items.forEach((item, index) => {
      const segment = document.createElement('button');
      segment.type = 'button';
      segment.tabIndex = -1;
      segment.className = 's-gallery-rail__segment';
      segment.setAttribute('aria-label', `Image ${index + 1}`);
      segment.addEventListener('click', () => go(index));
      track.appendChild(segment);
    });
    prev.addEventListener('click', () => go(current - 1));
    next.addEventListener('click', () => go(current + 1));

    const host = list.closest('slider-component') || list.parentElement;
    host.appendChild(rail);

    /* in the margin, along the photograph's left edge; hidden if the margin is too narrow */
    const place = () => {
      const item = items[current];
      const frame = item && (item.querySelector('.product-media-container') || item);
      if (!frame || !frame.offsetWidth) return rail.classList.remove('is-placed');
      const a = frame.getBoundingClientRect();
      const b = host.getBoundingClientRect();
      const width = rail.offsetWidth || 28;
      const fits = a.left - width - GAP >= 0;
      rail.classList.toggle('is-placed', fits);
      if (!fits) return;
      rail.style.left = `${Math.round(a.left - b.left - width - GAP)}px`;
      rail.style.top = `${Math.round(a.top - b.top)}px`;
      rail.style.height = `${Math.round(a.height)}px`;
    };

    const update = () => {
      let index = items.findIndex((item) => item.classList.contains('is-active'));
      if (index < 0) index = 0;
      const changed = index !== current;
      current = index;
      rail.style.setProperty('--s-rail-i', index);
      number.textContent = pad(index + 1);
      if (changed && Sulfur.decode) {
        delete number.dataset.sText;
        Sulfur.decode(number, 380);
      }
      prev.disabled = index === 0;
      next.disabled = index === items.length - 1;
      place();
    };
    update();
    new MutationObserver(update).observe(list, { attributes: true, subtree: true, attributeFilter: ['class'] });
    if ('ResizeObserver' in window) new ResizeObserver(place).observe(list);
    window.addEventListener('resize', Sulfur.frame(place));
    window.addEventListener('load', place);
  };

  const init = (scope) => scope.querySelectorAll('media-gallery').forEach(build);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document));
  else init(document);
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
/*
 * Page transitions (cross-document View Transitions, where the browser supports them).
 * CSS does the cross-fade; this file only names the product photograph that should travel
 * between a product card and the product page.
 */
(() => {
  'use strict';
  const root = document.documentElement;
  if (!root.classList.contains('sulfur-vt') || !('startViewTransition' in document)) return;
  const NAME = 's-product-media';
  const handleOf = (url) => {
    const match = /\/products\/([^/?#]+)/.exec(url || '');
    return match ? decodeURIComponent(match[1]) : null;
  };
  const cardImage = (link) => {
    const card = link.closest('.card-wrapper, .s-buybar, .predictive-search__item, .cart-item');
    return card ? card.querySelector('.card__media img, img') : null;
  };
  const clear = () => document.querySelectorAll('[data-s-vt]').forEach((el) => {
    el.style.viewTransitionName = '';
    delete el.dataset.sVt;
  });
  const tag = (el) => {
    if (!el) return;
    clear();
    el.style.viewTransitionName = NAME;
    el.dataset.sVt = 'true';
  };

  /* Leaving a listing: the clicked card hands over its photograph */
  document.addEventListener(
    'click',
    (event) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const link = event.target.closest('a[href*="/products/"]');
      if (link) tag(cardImage(link));
    },
    true
  );

  /* Arriving: on a product page the first photograph receives it; coming back to a listing,
     the card of the product that was just viewed takes it back */
  window.addEventListener('pagereveal', (event) => {
    if (!event.viewTransition) return;
    const main = document.querySelector('product-info[id^="MainProduct-"] .product__media-item.is-active .product__media img, product-info[id^="MainProduct-"] .product__media-item .product__media img');
    if (main) {
      tag(main);
    } else {
      const from = window.navigation && navigation.activation && navigation.activation.from ? handleOf(navigation.activation.from.url) : null;
      if (from) {
        const link = [...document.querySelectorAll('.card-wrapper a[href*="/products/"]')].find((a) => handleOf(a.getAttribute('href')) === from);
        if (link) tag(cardImage(link));
      }
    }
    event.viewTransition.finished.finally(clear);
  });

  /* Pages restored from the back/forward cache must not keep a stale name */
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) clear();
  });
})();
/* System details: pointer reticle and halo, hero read-outs, magnetic controls */
(() => {
  'use strict';
  const Sulfur = window.Sulfur;
  const root = document.documentElement;
  const french = (root.lang || 'fr').toLowerCase().startsWith('fr');

  /* Reticle + halo: decorative followers. The system cursor is left untouched. */
  const pointer = () => {
    if (!root.classList.contains('sulfur-cursor') || Sulfur.reduced || !Sulfur.finePointer.matches) return;
    if (window.Shopify && window.Shopify.designMode) return;
    const halo = document.createElement('div');
    halo.className = 's-halo';
    const cursor = document.createElement('div');
    cursor.className = 's-cursor';
    cursor.innerHTML = '<span class="s-cursor__ring"></span><span class="s-cursor__label"></span>';
    [halo, cursor].forEach((el) => {
      el.setAttribute('aria-hidden', 'true');
      document.body.appendChild(el);
    });
    const label = cursor.querySelector('.s-cursor__label');
    const WORDS = french
      ? { view: 'Voir', drag: 'Glisser', explore: 'Explorer', play: 'Lire', zoom: 'Agrandir', copy: 'Copier' }
      : { view: 'View', drag: 'Drag', explore: 'Explore', play: 'Play', zoom: 'Zoom', copy: 'Copy' };
    const RULES = [
      ['.deferred-media__poster', 'play'],
      ['.product__modal-opener', 'zoom'],
      ['.card-wrapper', 'view'],
      ['.s-uni__card[href]', 'explore'],
      ['.s-rail__more', 'view'],
      ['.s-rail--rail .s-rail__track', 'drag'],
    ];
    const target = { x: window.innerWidth / 2, y: window.innerHeight / 2 };
    const ring = { ...target };
    const glow = { ...target };
    let running = false;
    let idle = 0;

    const loop = () => {
      ring.x += (target.x - ring.x) * 0.28;
      ring.y += (target.y - ring.y) * 0.28;
      glow.x += (target.x - glow.x) * 0.07;
      glow.y += (target.y - glow.y) * 0.07;
      cursor.style.setProperty('--s-cx', `${ring.x.toFixed(1)}px`);
      cursor.style.setProperty('--s-cy', `${ring.y.toFixed(1)}px`);
      halo.style.setProperty('--s-hx', `${glow.x.toFixed(1)}px`);
      halo.style.setProperty('--s-hy', `${glow.y.toFixed(1)}px`);
      // stop the loop once everything has caught up with a still pointer
      idle = Math.abs(target.x - glow.x) + Math.abs(target.y - glow.y) < 0.5 ? idle + 1 : 0;
      if (idle > 20) running = false;
      else requestAnimationFrame(loop);
    };

    const read = (el) => {
      if (!el || !el.closest) return;
      let word = null;
      for (const [selector, key] of RULES) {
        if (el.closest(selector)) {
          word = WORDS[key];
          break;
        }
      }
      if (word) label.textContent = word;
      cursor.classList.toggle('has-label', !!word);
      cursor.classList.toggle('is-link', !word && !!el.closest('a, button, summary, label, select, [role="button"], input, textarea'));
    };

    window.addEventListener(
      'pointermove',
      (event) => {
        if (event.pointerType && event.pointerType !== 'mouse') return;
        target.x = event.clientX;
        target.y = event.clientY;
        root.classList.add('has-pointer');
        if (!running) {
          running = true;
          idle = 0;
          requestAnimationFrame(loop);
        }
      },
      { passive: true }
    );
    document.addEventListener('pointerover', (event) => read(event.target), { passive: true });
    document.addEventListener('pointerdown', () => cursor.classList.add('is-down'), { passive: true });
    document.addEventListener('pointerup', () => cursor.classList.remove('is-down'), { passive: true });
    document.documentElement.addEventListener('pointerleave', () => root.classList.remove('has-pointer'));
  };

  /* Hero read-outs: the visitor's clock, and the number of strikes since they arrived */
  const hud = (scope) => {
    scope.querySelectorAll('.s-hero__hud:not([data-s-live])').forEach((el) => {
      el.dataset.sLive = 'true';
      const hero = el.closest('sulfur-hero');
      const clock = el.querySelector('[data-hud-clock]');
      if (clock) {
        const pad = (n) => String(n).padStart(2, '0');
        const tick = () => {
          if (!el.isConnected) return clearInterval(timer);
          const now = new Date();
          clock.textContent = `${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;
        };
        const timer = setInterval(tick, 1000);
        tick();
      }
      const strikes = el.querySelector('[data-hud-strikes]');
      if (hero && strikes) {
        const count = strikes.querySelector('[data-hud-count]');
        hero.addEventListener('sulfur:strike', (event) => {
          strikes.hidden = false;
          count.textContent = String(event.detail.count).padStart(2, '0');
          strikes.classList.add('is-hit');
          setTimeout(() => strikes.classList.remove('is-hit'), 500);
        });
      }
    });
  };

  /* Magnetic controls: a few key buttons lean towards the pointer */
  const magnets = (scope) => {
    if (Sulfur.reduced || !Sulfur.finePointer.matches) return;
    scope.querySelectorAll('.s-hero__actions .button, .s-rail__button, .s-copy__button, .deferred-media__poster-button').forEach((el) => {
      if (el.dataset.sMagnet != null) return;
      el.dataset.sMagnet = '';
      const zone = el.closest('.deferred-media__poster') || el;
      zone.addEventListener('pointermove', (event) => {
        const rect = el.getBoundingClientRect();
        const dx = event.clientX - (rect.left + rect.width / 2);
        const dy = event.clientY - (rect.top + rect.height / 2);
        el.classList.add('is-pulled');
        el.style.setProperty('--s-mx', `${Sulfur.clamp(dx * 0.22, -14, 14).toFixed(1)}px`);
        el.style.setProperty('--s-my', `${Sulfur.clamp(dy * 0.3, -10, 10).toFixed(1)}px`);
      });
      zone.addEventListener('pointerleave', () => {
        el.classList.remove('is-pulled');
        el.style.setProperty('--s-mx', '0px');
        el.style.setProperty('--s-my', '0px');
      });
    });
  };

  /* Collection names in the index re-decode when they are aimed at */
  const indexHover = (scope) => {
    if (Sulfur.reduced || !Sulfur.finePointer.matches) return;
    scope.querySelectorAll('.s-index__link:not([data-s-hover])').forEach((link) => {
      link.dataset.sHover = 'true';
      const name = link.querySelector('.s-index__name');
      if (name) link.addEventListener('pointerenter', () => Sulfur.decode(name, 320));
    });
  };

  const init = (scope) => {
    hud(scope);
    magnets(scope);
    indexHover(scope);
  };
  const start = () => {
    pointer();
    init(document);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
/*
 * Scroll and swipe choreography, written for phones first.
 * Everything here is driven by the visitor's own gesture: how fast the page or a rail is
 * moving, which element sits in focus. Nothing runs while the finger is still.
 */
(() => {
  'use strict';
  const Sulfur = window.Sulfur;
  const root = document.documentElement;
  if (!root.classList.contains('sulfur-touch') || Sulfur.reduced) return;
  if (window.Shopify && window.Shopify.designMode) return;
  const coarse = window.matchMedia('(hover: none)');

  /* 1. Reveal: visuals open along their centre line the first time they are reached -------- */
  const REVEAL = [
    '.card-wrapper',
    '.s-crew__item',
    '.s-uni__panel',
    '.s-index__row',
    '.s-manifesto__foot',
    '.video-section',
    '.product__media-list > .product__media-item:not(:first-child)',
    '.s-assure',
    '.footer-block',
  ].join(',');
  // The clip that hides an element must sit on a child, never on the observed element itself:
  // browsers treat a fully clipped target as "not intersecting" and it would stay hidden.
  // A plain position check on scroll backs the observer up, so nothing can remain unrevealed.
  const pending = new Set();
  let order = 0;
  let orderReset = 0;
  const open = (el) => {
    if (!pending.has(el)) return;
    pending.delete(el);
    if (revealer) revealer.unobserve(el);
    // elements that arrive together open one after the other
    el.style.setProperty('--s-order', String(Math.min(order++, 5)));
    clearTimeout(orderReset);
    orderReset = setTimeout(() => (order = 0), 120);
    el.classList.add('is-in');
  };
  const sweep = Sulfur.frame(() => {
    if (!pending.size) return;
    const height = window.innerHeight;
    const width = window.innerWidth;
    pending.forEach((el) => {
      const rect = el.getBoundingClientRect();
      if (rect.width === 0 && rect.height === 0) return; // not displayed (hidden variant photo, closed drawer)
      if (rect.top < height * 0.94 && rect.bottom > 0 && rect.left < width && rect.right > 0) open(el);
    });
  });
  let revealer = null;
  root.classList.add('s-reveal-on');
  if ('IntersectionObserver' in window) {
    revealer = new IntersectionObserver(
      (entries) => entries.forEach((entry) => (entry.isIntersecting || entry.intersectionRatio > 0) && open(entry.target)),
      { rootMargin: '0px 0px -6% 0px', threshold: 0.08 }
    );
  }
  window.addEventListener('scroll', sweep, { passive: true });
  window.addEventListener('resize', sweep);
  document.addEventListener('scroll', sweep, { passive: true, capture: true }); // horizontal rails and galleries
  const watch = (scope) => {
    scope.querySelectorAll(REVEAL).forEach((el) => {
      if (el.dataset.sReveal) return;
      el.dataset.sReveal = 'true';
      el.classList.add('s-reveal');
      pending.add(el);
      if (revealer) revealer.observe(el);
    });
    sweep();
  };

  /* 2. Page velocity: titles lean and tickers accelerate with the speed of the swipe -------- */
  const leaning = new Set();
  const tickers = new Set();
  const visible =
    'IntersectionObserver' in window
      ? new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            const set = entry.target.matches('.s-ticker') ? tickers : leaning;
            if (entry.isIntersecting) set.add(entry.target);
            else {
              set.delete(entry.target);
              entry.target.style.removeProperty('--s-vel');
            }
          });
        })
      : null;
  const track = (scope) => {
    if (!visible) return;
    scope.querySelectorAll('.s-head__title, .s-index__name, .s-ticker, .s-wordmark__svg, .s-manifesto__statement').forEach((el) => {
      if (el.dataset.sVel) return;
      el.dataset.sVel = 'true';
      visible.observe(el);
    });
  };

  let lastY = window.scrollY;
  let lastT = performance.now();
  let velocity = 0;
  let goal = 0;
  let direction = 1;
  let spinning = false;

  const spin = () => {
    velocity += (goal - velocity) * 0.2;
    goal *= 0.86;
    const settled = Math.abs(velocity) < 0.004 && Math.abs(goal) < 0.004;
    const value = settled ? 0 : velocity;
    leaning.forEach((el) => el.style.setProperty('--s-vel', value.toFixed(3)));
    tickers.forEach((el) => {
      const rate = direction * (1 + Math.abs(value) * 5);
      el.querySelectorAll('.s-ticker__track').forEach((line) => {
        (line.getAnimations ? line.getAnimations() : []).forEach((animation) => {
          // "reverse" tickers already run backwards: keep their own sense of direction
          animation.playbackRate = rate;
        });
      });
    });
    if (settled) {
      velocity = 0;
      spinning = false;
      return;
    }
    requestAnimationFrame(spin);
  };

  window.addEventListener(
    'scroll',
    () => {
      const now = performance.now();
      const y = window.scrollY;
      const dt = Math.max(8, now - lastT);
      const speed = (y - lastY) / dt; // px per ms
      lastY = y;
      lastT = now;
      if (speed !== 0) direction = speed > 0 ? 1 : -1;
      goal = Sulfur.clamp(speed / 3, -1, 1);
      if (!spinning) {
        spinning = true;
        requestAnimationFrame(spin);
      }
    },
    { passive: true }
  );

  /* 3. Horizontal swipes: rails and galleries carry inertia, and one item is always in focus -- */
  const swipeable = (scope) => {
    scope.querySelectorAll('.s-rail--rail .s-rail__track, .s-uni__viewport, .product__media-list.slider, .blog__posts.slider, .thumbnail-list').forEach((scroller) => {
      if (scroller.dataset.sSwipe) return;
      scroller.dataset.sSwipe = 'true';
      const items = () => [...scroller.querySelectorAll(':scope > li, :scope > .s-uni__track > li')];
      const host = scroller.closest('media-gallery, sulfur-rail, sulfur-universe, slider-component') || scroller;
      let x = scroller.scrollLeft;
      let t = performance.now();
      let v = 0;
      let target = 0;
      let active = false;

      const focus = () => {
        const list = items();
        if (!list.length) return;
        const box = scroller.getBoundingClientRect();
        const anchor = box.left + Math.min(box.width * 0.35, 220);
        let best = null;
        let bestDistance = Infinity;
        list.forEach((item) => {
          const rect = item.getBoundingClientRect();
          if (!rect.width) return item.style.setProperty('--s-par', '0'); // hidden slide: no drift kept for later
          const distance = Math.abs(rect.left + rect.width / 2 - anchor);
          // how far the item is from the centre of the scroller, from -1 to 1
          item.style.setProperty('--s-par', Sulfur.clamp((rect.left + rect.width / 2 - (box.left + box.width / 2)) / box.width, -1, 1).toFixed(3));
          if (distance < bestDistance) {
            bestDistance = distance;
            best = item;
          }
        });
        list.forEach((item) => item.classList.toggle('is-focus', item === best));
        const max = scroller.scrollWidth - scroller.clientWidth;
        host.style.setProperty('--s-gp', max > 0 ? (scroller.scrollLeft / max).toFixed(4) : '0');
        host.classList.toggle('is-swipeable', max > 4);
      };

      const loop = () => {
        v += (target - v) * 0.25;
        target *= 0.82;
        const still = Math.abs(v) < 0.004 && Math.abs(target) < 0.004;
        scroller.style.setProperty('--s-swipe', still ? '0' : v.toFixed(3));
        if (still) {
          active = false;
          scroller.classList.remove('is-swiping');
          return;
        }
        requestAnimationFrame(loop);
      };

      const onScroll = Sulfur.frame(() => {
        const now = performance.now();
        const nx = scroller.scrollLeft;
        const dt = Math.max(8, now - t);
        target = Sulfur.clamp((nx - x) / dt / 2.2, -1, 1);
        x = nx;
        t = now;
        focus();
        if (!active) {
          active = true;
          scroller.classList.add('is-swiping');
          requestAnimationFrame(loop);
        }
      });
      scroller.addEventListener('scroll', onScroll, { passive: true });
      if ('ResizeObserver' in window) new ResizeObserver(Sulfur.frame(focus)).observe(scroller);
      focus();
    });
  };

  /* 4. Index: on a touch screen the row crossing the middle of the screen lights up ---------- */
  const rows = [];
  const lightRows = Sulfur.frame(() => {
    if (!coarse.matches || !rows.length) return;
    const middle = window.innerHeight * 0.48;
    let best = null;
    let bestDistance = window.innerHeight * 0.22;
    rows.forEach((row) => {
      const rect = row.getBoundingClientRect();
      const distance = Math.abs(rect.top + rect.height / 2 - middle);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = row;
      }
    });
    rows.forEach((row) => row.classList.toggle('is-focus', row === best));
  });
  const indexRows = (scope) => {
    scope.querySelectorAll('.s-index__row').forEach((row) => {
      if (!rows.includes(row)) rows.push(row);
    });
    lightRows();
  };
  window.addEventListener('scroll', lightRows, { passive: true });

  const init = (scope) => {
    watch(scope);
    track(scope);
    swipeable(scope);
    indexRows(scope);
  };
  const start = () => init(document);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
  // product grids are replaced when filters change, recommendations arrive later
  if ('MutationObserver' in window) {
    const refresh = Sulfur.frame(() => init(document));
    const observe = () => {
      const main = document.getElementById('MainContent');
      if (main) new MutationObserver(refresh).observe(main, { childList: true, subtree: true });
    };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', observe);
    else observe();
  }
})();
/* Sector header of collection pages */
(() => {
  'use strict';
  const Sulfur = window.Sulfur;
  const root = document.documentElement;

  /* Sector header: count that runs up, current collection scrolled into view, grid density */
  const GRID_KEY = 'sulfur-grid';
  const readGrid = () => {
    try {
      return window.localStorage.getItem(GRID_KEY) === 'large' ? 'large' : 'dense';
    } catch (error) {
      return 'dense';
    }
  };
  /* Bigger plates need bigger files: tell the browser how wide the photographs now are */
  const resize = () => {
    const large = root.dataset.sGrid === 'large';
    document.querySelectorAll('#product-grid .card__media img').forEach((img) => {
      if (img.dataset.sSizes === undefined) img.dataset.sSizes = img.getAttribute('sizes') || '';
      const wanted = large ? '(min-width: 1200px) 34vw, (min-width: 750px) 50vw, 100vw' : img.dataset.sSizes;
      if (img.getAttribute('sizes') !== wanted) img.setAttribute('sizes', wanted);
    });
  };
  const applyGrid = (mode) => {
    if (mode === 'large') root.dataset.sGrid = 'large';
    else delete root.dataset.sGrid;
    document.querySelectorAll('[data-sector-grid]').forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.sectorGrid === mode)));
    resize();
  };

  if (!customElements.get('sulfur-sector')) {
    customElements.define(
      'sulfur-sector',
      class extends HTMLElement {
        connectedCallback() {
          const current = this.querySelector('.s-sector__chip.is-current');
          const chips = this.querySelector('.s-sector__chips');
          if (current && chips) chips.scrollLeft = Math.max(0, current.offsetLeft - chips.offsetLeft - 16);

          const density = this.querySelector('.s-sector__density');
          const grid = document.getElementById('product-grid');
          if (density && grid) {
            density.hidden = false;
            applyGrid(readGrid());
            density.addEventListener('click', (event) => {
              const button = event.target.closest('[data-sector-grid]');
              if (!button) return;
              const mode = button.dataset.sectorGrid;
              try {
                window.localStorage.setItem(GRID_KEY, mode);
              } catch (error) {
                /* private mode: the choice simply lasts for this page */
              }
              applyGrid(mode);
            });
          }

          this.count = this.querySelector('[data-sector-count]');
          if (this.count) {
            const total = Number(this.count.dataset.sectorCount) || 0;
            if (!Sulfur.reduced && total > 1) this.run(total);
          }

          // Dawn re-renders the grid and its count when filters change: follow it
          const container = document.getElementById('ProductGridContainer');
          if (container && 'MutationObserver' in window) {
            this.observer = new MutationObserver(() => {
              resize();
              const label = document.getElementById('ProductCountDesktop') || document.getElementById('ProductCount');
              const match = label && label.textContent.match(/\d+/);
              if (match && this.count) this.set(Number(match[0]));
            });
            this.observer.observe(container, { childList: true });
          }
        }

        disconnectedCallback() {
          if (this.observer) this.observer.disconnect();
          cancelAnimationFrame(this.raf);
        }

        set(value) {
          cancelAnimationFrame(this.raf);
          this.count.textContent = String(value).padStart(2, '0');
        }

        run(total) {
          const start = performance.now();
          const duration = Sulfur.clamp(total * 45, 500, 1100);
          const step = (now) => {
            const t = Sulfur.clamp((now - start) / duration, 0, 1);
            const eased = 1 - Math.pow(1 - t, 3);
            this.count.textContent = String(Math.round(total * eased)).padStart(2, '0');
            if (t < 1) this.raf = requestAnimationFrame(step);
          };
          this.count.textContent = '00';
          this.raf = requestAnimationFrame(step);
        }
      }
    );
  }
})();
